import React, { useMemo } from "react";
import {
  Box,
  Button,
  Chip,
  Divider,
  Link,
  Paper,
  Stack,
  Typography,
} from "@mui/material";
import { OpenInNewRounded } from "@mui/icons-material";
import { DocumentIntelligenceDocument } from "../../api/documentIntelligence";

interface DocumentTimelineProps {
  documents: DocumentIntelligenceDocument[];
  onOpenDocument: (document: DocumentIntelligenceDocument) => void;
}

const titleCase = (value: string): string =>
  value
    .split("_")
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(" ");

const dateLabel = (value?: string | null): string => {
  if (!value) return "Date unavailable";
  return new Intl.DateTimeFormat("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
    timeZone: "UTC",
  }).format(new Date(value));
};

export const DocumentTimeline: React.FC<DocumentTimelineProps> = ({
  documents,
  onOpenDocument,
}) => {
  const chronologicalDocuments = useMemo(
    () =>
      [...documents].sort((left, right) => {
        const leftTime = Date.parse(left.review_date);
        const rightTime = Date.parse(right.review_date);
        return rightTime - leftTime || left.title.localeCompare(right.title);
      }),
    [documents],
  );

  if (!chronologicalDocuments.length) {
    return (
      <Typography color="text.secondary">
        No documents in this window.
      </Typography>
    );
  }

  return (
    <Stack spacing={2}>
      {chronologicalDocuments.map((document) => (
        <Paper
          key={`${document.source_type}:${document.content_id}`}
          variant="outlined"
          sx={{ p: 2.5 }}
        >
          <Stack spacing={2}>
            <Stack
              direction={{ xs: "column", md: "row" }}
              justifyContent="space-between"
              spacing={1.5}
            >
              <Box>
                <Stack
                  direction="row"
                  spacing={1}
                  alignItems="center"
                  flexWrap="wrap"
                  useFlexGap
                >
                  <Chip
                    size="small"
                    label={
                      document.last_corporate_transaction_pipeline_run_status
                        ? `CTX ${titleCase(document.last_corporate_transaction_pipeline_run_status)}`
                        : "CTX not run"
                    }
                    color={
                      document.last_corporate_transaction_pipeline_run_status ===
                        "completed" ||
                      document.last_corporate_transaction_pipeline_run_status ===
                        "empty"
                        ? "success"
                        : document.last_corporate_transaction_pipeline_run_status ===
                            "failed"
                          ? "error"
                          : document.last_corporate_transaction_pipeline_run_status ===
                              "partial"
                            ? "warning"
                            : "default"
                    }
                    variant="outlined"
                  />
                  <Chip
                    size="small"
                    label={
                      document.source_type === "press_release"
                        ? "PR"
                        : "Presentation"
                    }
                    color={
                      document.source_type === "press_release"
                        ? "info"
                        : "secondary"
                    }
                    variant="outlined"
                  />
                  {document.company_name && (
                    <Chip
                      size="small"
                      label={document.company_name}
                      variant="outlined"
                    />
                  )}
                  <Typography component="h2" variant="h6" fontWeight={700}>
                    {document.title}
                  </Typography>
                </Stack>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ mt: 0.5 }}
                >
                  {dateLabel(document.published_date)}
                </Typography>
              </Box>
              <Stack direction="row" spacing={1} alignItems="center">
                {document.url && (
                  <Link
                    href={document.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    sx={{
                      display: "inline-flex",
                      alignItems: "center",
                      gap: 0.5,
                    }}
                  >
                    Source <OpenInNewRounded fontSize="inherit" />
                  </Link>
                )}
                <Button
                  variant="outlined"
                  onClick={() => onOpenDocument(document)}
                >
                  Open document review
                </Button>
              </Stack>
            </Stack>

            <Divider />

            <Stack direction={{ xs: "column", md: "row" }} spacing={3}>
              <Box sx={{ flex: 1 }}>
                <Typography variant="subtitle2" fontWeight={700} gutterBottom>
                  Milestones ({document.milestones?.length || 0})
                </Typography>
                {!document.milestones?.length ? (
                  <Typography variant="body2" color="text.secondary">
                    No milestones identified
                  </Typography>
                ) : (
                  <Stack spacing={1}>
                    {document.milestones.map((milestone, index) => (
                      <Box
                        key={milestone.id || `${milestone.milestone}-${index}`}
                      >
                        <Stack
                          direction="row"
                          spacing={1}
                          alignItems="center"
                          flexWrap="wrap"
                          useFlexGap
                        >
                          <Typography variant="body2">
                            {milestone.milestone}
                          </Typography>
                          <Chip
                            size="small"
                            label={titleCase(milestone.status)}
                          />
                        </Stack>
                        {milestone.date_label && (
                          <Typography variant="caption" color="text.secondary">
                            {milestone.date_label}
                          </Typography>
                        )}
                      </Box>
                    ))}
                  </Stack>
                )}
              </Box>

              <Divider
                orientation="vertical"
                flexItem
                sx={{ display: { xs: "none", md: "block" } }}
              />

              <Box sx={{ flex: 1 }}>
                <Typography variant="subtitle2" fontWeight={700} gutterBottom>
                  Corporate transactions (
                  {document.corporate_transactions?.length || 0})
                </Typography>
                {!document.corporate_transactions?.length ? (
                  <Typography variant="body2" color="text.secondary">
                    No corporate transactions identified
                  </Typography>
                ) : (
                  <Stack spacing={1}>
                    {document.corporate_transactions.map(
                      (transaction, index) => (
                        <Stack
                          key={
                            transaction.id || transaction.candidate_id || index
                          }
                          direction="row"
                          spacing={1}
                          alignItems="center"
                          flexWrap="wrap"
                          useFlexGap
                        >
                          <Chip
                            size="small"
                            label={titleCase(transaction.transaction_type)}
                            color="primary"
                            variant="outlined"
                          />
                          <Chip
                            size="small"
                            label={
                              transaction.origin === "review_candidate"
                                ? "Pending Review"
                                : titleCase(transaction.status || "stored")
                            }
                            color={
                              transaction.origin === "review_candidate"
                                ? "warning"
                                : "default"
                            }
                          />
                          <Typography variant="caption" color="text.secondary">
                            {titleCase(transaction.transaction_class)}
                          </Typography>
                        </Stack>
                      ),
                    )}
                  </Stack>
                )}
              </Box>
            </Stack>
          </Stack>
        </Paper>
      ))}
    </Stack>
  );
};
